import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { FaClipboardList } from "react-icons/fa";
import dayjs from "dayjs";
import styles from "./Create.module.css";
import {
  CustomButton,
  CustomDatePicker,
  CustomDropdown,
  CustomTextInput,
  CustomFileInput,
} from "../../components";
import { fetchLeaveReasonOptions } from "../../redux/Masterdata";
import AxiosInstance, { TokenService } from "../../services/Api";
import PopupModal from "../../components/PopupModal/PopupModal";

const LeaveCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const editId = location.state?.ID || null;

  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const client_id = clientData?.client_id || "";
  const school_id = clientData?.school_id || "";
  const parent_id = clientData?.master_id || "";
  const user_id = user?.id || "";

  const { translations } = useSelector((state) => state.language);

  // Get reason options from redux masterData
  const {
    leaveReasonOptions: reduxReasonOptions,
    leaveReasonLoading: reasonLoading,
  } = useSelector((state) => state.masterData);

  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Dropdown options
  const [childOptions, setChildOptions] = useState([]);
  const [staffName, setStaffName] = useState("");
  const [staffId, setStaffId] = useState("");
  const [studentClassId, setStudentClassId] = useState("");
  const [studentSectionId, setStudentSectionId] = useState("");

  const [leaveStatus, setLeaveStatus] = useState(null);
  const [formData, setFormData] = useState({
    studentId: "",
    fromDate: null,
    toDate: null,
    reason: "",
    noteMsg: "",
    original_name: "",
    upload_path: "",
  });
  // Modal Popup
  const [modalState, setModalState] = useState({
    open: false,
    type: "success", // "success" | "error"
    message: "",
    redirectPath: "",
  });
  // Build reason dropdown options from redux
  const reasonOptions = useMemo(
    () =>
      (reduxReasonOptions || []).map((item) => ({
        value: item.value,
        label: item.label,
      })),
    [reduxReasonOptions],
  );

  // Auto-calculate No. of Days
  const noDays = useMemo(() => {
    if (formData.fromDate && formData.toDate) {
      const from = dayjs(formData.fromDate);
      const to = dayjs(formData.toDate);
      const diff = to.diff(from, "day") + 1;
      return diff > 0 ? diff : 0;
    }
    return 0;
  }, [formData.fromDate, formData.toDate]);

  // Fetch reason options from masterData on mount
  useEffect(() => {
    dispatch(fetchLeaveReasonOptions());
  }, [dispatch]);

  // Fetch children list on mount
  useEffect(() => {
    if (parent_id) {
      fetchChildren();
    }
  }, [parent_id]);

  // Fetch staff when child is selected
  useEffect(() => {
    if (formData.studentId) {
      fetchStaff(formData.studentId);
    } else {
      setStaffName("");
      setStaffId("");
      setStudentClassId("");
      setStudentSectionId("");
    }
  }, [formData.studentId]);

  // If editing, load existing data
  useEffect(() => {
    if (editId) {
      fetchLeaveDetails();
    }
  }, [editId]);

  const fetchChildren = async () => {
    try {
      const response = await AxiosInstance.post(`/attendance/parent/students`, {
        parent_id: parseInt(parent_id),
      });
      if (response?.data?.status) {
        const students = response.data.data || [];
        setChildOptions(
          students.map((s) => ({
            value: s.student_id,
            label: s.student_name,
            class_id: s.class_id,
            section_id: s.section_id,
          })),
        );
      }
    } catch (error) {
      console.error("Error fetching children:", error);
    }
  };

  const fetchStaff = async (studentId) => {
    try {
      const response = await AxiosInstance.post(`/attendance/student/staff`, {
        student_id: parseInt(studentId),
      });
      if (response?.data?.status) {
        const staffData = response.data.data || [];
        if (staffData.length > 0) {
          setStaffName(staffData[0].staff_name || "");
          setStaffId(staffData[0].staff_id || "");
        }
      }
    } catch (error) {
      console.error("Error fetching staff:", error);
    }
  };

  const fetchLeaveDetails = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.post(`/attendance/viewLeave`, {
        id: editId,
      });

      if (response?.data?.status || response?.data?.success) {
        const data = Array.isArray(response.data.data)
          ? response.data.data[0]
          : response.data.data;
        setLeaveStatus(data.status);
        setFormData({
          studentId: data.student_id || "",
          fromDate: data.from_date
            ? dayjs(data.from_date).format("YYYY-MM-DD")
            : null,
          toDate: data.to_date
            ? dayjs(data.to_date).format("YYYY-MM-DD")
            : null,
          reason: data.reason || "",
          noteMsg: data.note_msg || data.messages || "",
          original_name: data.original_name || "",
          upload_path: data.upload_path || "",
        });

        if (data.class_id) setStudentClassId(data.class_id);
        if (data.section_id) setStudentSectionId(data.section_id);
      }
    } catch (error) {
      console.error("Error fetching leave details:", error);
      alert(
        translations.labels?.fetchFailed ||
          "Failed to fetch details. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDropdownChange = (name, selectedOption) => {
    const value = selectedOption?.value ?? selectedOption ?? "";

    if (name === "studentId") {
      const selectedChild = childOptions.find((c) => c.value === value);
      setStudentClassId(selectedChild?.class_id || "");
      setStudentSectionId(selectedChild?.section_id || "");
      setStaffName("");
      setStaffId("");
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleDateChange = (name, date) => {
    setFormData((prev) => ({
      ...prev,
      [name]: date ? dayjs(date).format("YYYY-MM-DD") : null,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // File upload handler with API call and validation
  const handleFileChange = useCallback(async (file) => {
    if (!file) {
      setFormData((prev) => ({
        ...prev,
        original_name: "",
        upload_path: "",
      }));
      setErrors((prev) => ({ ...prev, attachment: "" }));
      return;
    }

    // Validate file type - images, pdf, and word documents
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    if (!allowedTypes.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        attachment:
          "Only jpg, jpeg, png, gif, pdf, doc, docx files are allowed",
      }));
      return;
    }

    try {
      const formDataPayload = new FormData();
      formDataPayload.append("content_image", file);
      formDataPayload.append("type", "Leave");
      formDataPayload.append("school_id", school_id);

      const response = await AxiosInstance.post(
        "/content/upload",
        formDataPayload,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      if (Array.isArray(response.data) && response.data.length > 0) {
        const uploadedFile = response.data[0];

        setFormData((prev) => ({
          ...prev,
          original_name: uploadedFile.original_name,
          upload_path: uploadedFile.upload_path,
        }));

        setErrors((prev) => ({ ...prev, attachment: "" }));
      } else {
        throw new Error("Invalid upload response");
      }
    } catch (error) {
      console.error("File upload error:", error);
      setErrors((prev) => ({
        ...prev,
        attachment: "Failed to upload file. Please try again.",
      }));
    }
  }, [school_id]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.studentId) {
      newErrors.studentId =
        translations.labels?.childRequired || "Child is required";
    }

    if (!formData.fromDate) {
      newErrors.fromDate =
        translations.labels?.fromDateRequired || "From Date is required";
    }

    if (!formData.toDate) {
      newErrors.toDate =
        translations.labels?.toDateRequired || "To Date is required";
    }

    if (
      formData.fromDate &&
      formData.toDate &&
      dayjs(formData.toDate).isBefore(dayjs(formData.fromDate))
    ) {
      newErrors.toDate =
        translations.labels?.toDateAfterFrom ||
        "To Date must be after From Date";
    }

    if (!formData.reason) {
      newErrors.reason =
        translations.labels?.reasonRequired || "Reason is required";
    }

    if (!formData.noteMsg || !formData.noteMsg.trim()) {
      newErrors.noteMsg =
        translations.labels?.noteRequired || "Note is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setSubmitting(true);

    try {
      let response;

      if (editId) {
        const payload = {
          from_date: formData.fromDate,
          to_date: formData.toDate,
          reason: formData.reason,
          no_days: noDays,
          messages: formData.noteMsg,
          note_msg: formData.noteMsg,
          status: 0,
          updated_by: parseInt(user_id),
          original_name: formData.original_name || null,
          upload_path: formData.upload_path || null,
          rattch_status: formData.upload_path ? 1 : 0,
        };

        response = await AxiosInstance.put(
          `/attendance/updateLeave/${editId}`,
          payload,
        );
      } else {
        const payload = {
          client_id: parseInt(client_id),
          school_id: parseInt(school_id),
          student_id: parseInt(formData.studentId),
          parent_id: parseInt(parent_id),
          class_id: parseInt(studentClassId),
          section_id: studentSectionId,
          staff_id: parseInt(staffId),
          from_date: formData.fromDate,
          to_date: formData.toDate,
          reason: formData.reason,
          no_days: noDays,
          messages: formData.noteMsg,
          note_msg: formData.noteMsg,
          created_by: parseInt(user_id),
          original_name: formData.original_name || null,
          upload_path: formData.upload_path || null,
          rattch_status: 0,
        };

        response = await AxiosInstance.post(`/attendance/createLeave`, payload);
      }

      if (response?.data?.status || response?.data?.success) {
        setModalState({
          open: true,
          type: "success",
          message: editId
            ? translations.labels?.leaveUpdateSuccess ||
              "Leave updated successfully!"
            : translations.labels?.leaveCreateSuccess ||
              "Leave request created successfully!",
          redirectPath: "/user/leave",
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message: response?.data?.message || "Something went wrong!",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error submitting leave:", error);

      setModalState({
        open: true,
        type: "error",
        message:
          translations.labels?.leaveSaveFailed ||
          "Failed to save leave request. Please try again.",
        redirectPath: "",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleWithdraw = async () => {
    setSubmitting(true);
    try {
      const payload = {
        from_date: formData.fromDate,
        to_date: formData.toDate,
        reason: formData.reason,
        no_days: noDays,
        messages: formData.noteMsg,
        note_msg: formData.noteMsg,
        status: 4,
        updated_by: parseInt(user_id),
        original_name: formData.original_name || null,
        upload_path: formData.upload_path || null,
        rattch_status: formData.upload_path ? 1 : 0,
      };

      const response = await AxiosInstance.put(
        `/attendance/updateLeave/${editId}`,
        payload,
      );

      if (response?.data?.status || response?.data?.success) {
        setModalState({
          open: true,
          type: "success",
          message:
            translations.labels?.leaveWithdrawSuccess ||
            "Leave withdrawn successfully!",
          redirectPath: "/user/leave",
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message: response?.data?.message || "Something went wrong!",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error withdrawing leave:", error);
      setModalState({
        open: true,
        type: "error",
        message:
          translations.labels?.leaveWithdrawFailed ||
          "Failed to withdraw leave. Please try again.",
        redirectPath: "",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/user/leave");
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaClipboardList />
          </div>
          <h1 className={styles.headerTitle}>
            {translations.labels?.leaveRequest || "Leave Request"}
          </h1>
        </div>
        <div className={styles.loadingContainer}>
          <p>{translations.labels?.loading || "Loading..."}</p>
        </div>
      </div>
    );
  }
  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({
      ...prev,
      open: false,
    }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };
  return (
    <>
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleSuccessOk}
      />
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaClipboardList />
          </div>
          <h1 className={styles.headerTitle}>
            {translations.labels?.leaveRequest || "Leave Request"}
          </h1>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="row">
            {/* Child Dropdown */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={translations.labels?.child || "Child"}
                name="studentId"
                value={formData.studentId}
                onChange={(value) => handleDropdownChange("studentId", value)}
                options={childOptions}
                placeholder={translations.labels?.selectChild || "Select Child"}
                isSearchable={false}
                required
                error={errors.studentId}
                disabled={!!editId}
              />
            </div>

            {/* Staff (Read-only) */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations.labels?.staffLabel || "Staff"}
                name="staffName"
                type="text"
                value={staffName}
                placeholder=""
                disabled
                readOnly
              />
            </div>

            <div className="col-lg-4 px-2 col-md-6 col-sm-12" />
          </div>

          <div className="row">
            {/* From Date */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDatePicker
                label={translations.labels?.fromDate || "From Date"}
                name="fromDate"
                value={formData.fromDate ? dayjs(formData.fromDate) : null}
                onChange={(date) => handleDateChange("fromDate", date)}
                placeholder="Ex: 30/07/2016"
                required
                error={errors.fromDate}
              />
            </div>

            {/* To Date */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDatePicker
                label={translations.labels?.toDate || "To Date"}
                name="toDate"
                value={formData.toDate ? dayjs(formData.toDate) : null}
                onChange={(date) => handleDateChange("toDate", date)}
                placeholder="Ex: 30/07/2016"
                required
                error={errors.toDate}
                minDate={formData.fromDate ? dayjs(formData.fromDate) : null}
              />
            </div>

            {/* No. of Days (Auto-calculated) */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations.labels?.noOfDaysLeave || "No. of Days"}
                name="noDays"
                type="text"
                value={noDays > 0 ? String(noDays) : ""}
                disabled
                readOnly
              />
            </div>
          </div>

          <div className="row">
            {/* Reason Dropdown */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={translations.labels?.reason || "Reason"}
                name="reason"
                value={formData.reason}
                onChange={(value) => handleDropdownChange("reason", value)}
                options={reasonOptions}
                placeholder={
                  translations.labels?.selectReason || "Select Reason"
                }
                isSearchable={false}
                required
                loading={reasonLoading}
                error={errors.reason}
              />
            </div>

            {/* Note */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations.labels?.noteLabel || "Note"}
                name="noteMsg"
                type="text"
                value={formData.noteMsg}
                onChange={handleInputChange}
                placeholder={
                  translations.labels?.notePlaceholder ||
                  "E.g. Enter your messages"
                }
                required
                error={errors.noteMsg}
              />
            </div>

            <div className="col-lg-4 px-2 col-md-6 col-sm-12" />
          </div>

          <div className="row">
            {/* File Attachment */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomFileInput
                label={translations.labels?.attachment || "Attachment"}
                name="attachment"
                value={formData.original_name || null}
                onChange={handleFileChange}
                accept="image/*,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                placeholder={
                  formData.original_name
                    ? formData.original_name
                    : "E.g. .jpg, .png, .pdf, .doc, .docx"
                }
                buttonText={translations.labels?.upload || "UPLOAD"}
                error={errors.attachment}
              />
            </div>
          </div>

          {/* Buttons */}

          <div className={styles.buttonContainer}>
            {leaveStatus !== 4 && (
              <CustomButton
                type="submit"
                variant="contained"
                className={styles.submitButton}
                fullWidth={false}
                loading={submitting}
                loadingText={
                  editId
                    ? translations.labels?.updating || "Updating..."
                    : translations.labels?.saving || "Saving..."
                }
              >
                {editId
                  ? translations.labels?.updateButton || "UPDATE"
                  : translations.labels?.saveButton || "SAVE"}
              </CustomButton>
            )}

            {editId && leaveStatus !== 4 && (
              <CustomButton
                type="button"
                variant="contained"
                className={styles.withdrawButton}
                fullWidth={false}
                loading={submitting}
                loadingText={
                  translations.labels?.withdrawing || "Withdrawing..."
                }
                onClick={handleWithdraw}
              >
                {translations.labels?.withdrawButton || "WITHDRAW"}
              </CustomButton>
            )}

            <CustomButton
              type="button"
              variant="contained"
              className={styles.cancelButton}
              fullWidth={false}
              onClick={handleCancel}
            >
              {translations.labels?.cancelButton || "CANCEL"}
            </CustomButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default LeaveCreate;
